import { StrictMode, Suspense } from "react";
import ReactDOM from "react-dom";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import Loading from "./features/core/features/loading/components/Loading";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { msalInstance } from "./utils/authentication";
import { AdminStore } from "./stores/AdminStore";
import { HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";
import { MsalProvider } from "@azure/msal-react";
import { AppRoutes } from "./AppRoutes";

const dsn = import.meta.env.VITE_ADMIN_SENTRY_DSN;
if (dsn) {
  Sentry.init({
    dsn,
    integrations: [new BrowserTracing()],
    environment: import.meta.env.MODE,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    release: import.meta.env.VITE_RELEASE,
  });
}

// Type definition for the Vite-specific event
interface VitePreloadErrorEvent extends Event {
  payload?: unknown;
}

window.addEventListener("vite:preloadError", (event: Event) => {
  const preloadErrorEvent = event as VitePreloadErrorEvent;
  const sentinel = "vite-reload-sentinel";
  const hasReloaded = window.sessionStorage.getItem(sentinel);

  // Avoid infinite reload loops if the server is actually down
  if (!hasReloaded) {
    // Log to Sentry so you can track deployment impact
    Sentry.captureMessage("Vite Preload Error - Forcing Reload", {
      level: "warning",
      extra: { url: window.location.href, payload: preloadErrorEvent.payload },
    });
    window.sessionStorage.setItem(sentinel, "true");
    window.location.reload();
  }
});

// Reset sentinel so future deploys can trigger a reload
window.sessionStorage.removeItem("vite-reload-sentinel");

const router = createBrowserRouter([
  {
    path: "*",
    element: <AppRoutes />,
  },
]);

ReactDOM.render(
  <StrictMode>
    <MsalProvider instance={msalInstance}>
      <Provider store={AdminStore}>
        <HelmetProvider>
          <Suspense fallback={<Loading />}>
            <RouterProvider router={router} fallbackElement={<Loading />} />
          </Suspense>
        </HelmetProvider>
      </Provider>
    </MsalProvider>
  </StrictMode>,
  document.getElementById("root"),
);
